import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Lightning, Plus } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { activeTab, TABS, type TabId } from '../../lib/layout';
import { haptic } from '../../lib/tma';
import { TapIcon } from '../icons/TapIcon';
import { ArchiveIcon } from '../icons/animated/ArchiveIcon';
import type { AnimatedIconHandle } from '../icons/animated/core';
import { UserIcon } from '../icons/animated/UserIcon';
import { UsersThreeIcon } from '../icons/animated/UsersThreeIcon';

/** The bottom menu of the main screens; it steps aside while the keyboard is open. */
export function TabBar() {
  const t = useT();
  const { pathname } = useLocation();
  const keyboardOpen = useKeyboardOpen();
  const current = activeTab(pathname);
  const [taps, setTaps] = useState<Record<TabId, number>>({ invite: 0, library: 0, create: 0, credits: 0, profile: 0 });
  const invite = useRef<AnimatedIconHandle>(null);
  const library = useRef<AnimatedIconHandle>(null);
  const profile = useRef<AnimatedIconHandle>(null);

  if (keyboardOpen) return null;

  const tapped = (id: TabId) => {
    haptic('light');
    setTaps((prev) => ({ ...prev, [id]: prev[id] + 1 }));
    ({ invite, library, profile })[id as 'invite' | 'library' | 'profile']?.current?.startAnimation();
  };

  const icon = (id: TabId): ReactNode => {
    switch (id) {
      case 'invite':
        return <UsersThreeIcon ref={invite} />;
      case 'library':
        return <ArchiveIcon ref={library} />;
      case 'profile':
        return <UserIcon ref={profile} />;
      case 'credits':
        return <TapIcon icon={Lightning} playKey={taps.credits} />;
      case 'create':
        return <TapIcon icon={Plus} weight="bold" playKey={taps.create} />;
    }
  };

  return (
    <nav
      aria-label={t.shell.menu}
      className="fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-line pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5 max-w-lg mx-auto h-16">
        {TABS.map(({ id, path }) => {
          const active = current === id;
          const label = t.shell.tabs[id];
          return (
            <li key={id} className="flex">
              <Link
                to={path}
                aria-current={active ? 'page' : undefined}
                onClick={() => tapped(id)}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] leading-[14px] select-none',
                  active ? 'text-fg font-semibold' : 'text-hint font-medium',
                )}
              >
                {id === 'create' ? (
                  <span className="-mt-6 inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent text-accent-on ring-4 ring-surface">
                    {icon(id)}
                  </span>
                ) : (
                  icon(id)
                )}
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
